import { useState, useEffect, useMemo, useCallback } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { carPricesApi, CarModelWithBrand, CarBrand, CarModelVersion } from '../../lib/carPricesApi';
import { Plus, Edit2, Copy, Trash2, Eye, EyeOff, Image as ImageIcon, Search, Filter, X, FileText, Check, AlertCircle, ChevronDown, ChevronRight, ArrowUpDown } from 'lucide-react';
import { ImageUpload } from '../../components/ImageUpload';

// Définition du type pour une caractéristique parsée
interface ParsedCharacteristic {
  key: string; // Clé de la base de données (ex: "garantie")
  displayKey: string; // Clé affichable (ex: "Garantie")
  value: string;
}

// Interface for SpecificationsDisplay props
interface SpecificationsDisplayProps {
  specifications: Record<string, any>;
  setSpecifications: (newSpecs: Record<string, any>) => void;
  clearAllSpecifications: () => void;
  sectionTitles: Record<string, string>;
  editingSectionTitle: string | null;
  tempSectionTitle: string;
  setTempSectionTitle: (title: string) => void;
  handleEditSectionTitle: (section: string) => void;
  handleSaveSectionTitle: () => void;
  handleCancelEditSectionTitle: () => void;
  handleSectionTitleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  expandedCategories: Record<string, boolean>;
  toggleCategoryExpansion: (category: string) => void;
}

// ## ----------------------------------------------------------------
// ## SpecificationsDisplay Component (Fully Editable)
// ## ----------------------------------------------------------------
const SpecificationsDisplay = ({
  specifications,
  setSpecifications,
  clearAllSpecifications,
  sectionTitles,
  editingSectionTitle,
  tempSectionTitle,
  setTempSectionTitle,
  handleEditSectionTitle,
  handleSaveSectionTitle,
  handleCancelEditSectionTitle,
  handleSectionTitleKeyDown,
  expandedCategories,
  toggleCategoryExpansion,
}: SpecificationsDisplayProps) => {

  // State for in-line spec editing
  const [editingSpec, setEditingSpec] = useState<{ category: string, key: string } | null>(null);
  const [tempSpecKey, setTempSpecKey] = useState('');
  const [tempSpecValue, setTempSpecValue] = useState('');

  // Handler to start editing a spec row
  const handleSpecEdit = (category: string, key: string, value: string) => {
    setEditingSpec({ category, key });
    setTempSpecKey(key);
    setTempSpecValue(String(value));
  };

  // Handler to cancel editing a spec row
  const handleSpecCancel = () => {
    setEditingSpec(null);
    setTempSpecKey('');
    setTempSpecValue('');
  };

  // Handler to save a spec row (key or value)
  const handleSpecSave = () => {
    if (!editingSpec) return;

    const { category: oldCategory, key: oldKey } = editingSpec;
    const newKey = tempSpecKey.trim();
    const newValue = tempSpecValue;

    if (!newKey) {
      alert("Le nom du champ ne peut pas être vide.");
      return;
    }

    const newSpecs = JSON.parse(JSON.stringify(specifications));

    // Check for key collision only if the key changed
    if (newKey !== oldKey && newSpecs[oldCategory][newKey]) {
      alert('Un champ avec ce nom existe déjà dans cette catégorie.');
      return;
    }

    // Delete the old key entry
    delete newSpecs[oldCategory][oldKey];
    // Add the new key entry (works for both rename and value change)
    newSpecs[oldCategory][newKey] = newValue;
    
    // Update the state
    setSpecifications(newSpecs);
    
    // Reset editing state
    handleSpecCancel();
  };


  // Handler to delete a specification
  const handleSpecDelete = (category: string, key: string) => {
    if (confirm(`Supprimer le champ "${key}" ?`)) {
      const newSpecs = JSON.parse(JSON.stringify(specifications)); // Deep copy
      if (newSpecs[category]) {
        delete newSpecs[category][key];
        setSpecifications(newSpecs);
      }
    }
  };

  // Handler to add a new specification
  const handleSpecAdd = (category: string) => {
    const newKey = prompt("Nom du nouveau champ (ex: Nombre de portes):");
    if (newKey && newKey.trim() !== "") {
      // Check if key already exists
      if (specifications[category] && specifications[category][newKey]) {
        alert("Ce champ existe déjà.");
        return;
      }
      
      const newValue = prompt(`Valeur pour "${newKey}":`);
      if (newValue !== null) { // Allow empty string
        const newSpecs = JSON.parse(JSON.stringify(specifications)); // Deep copy
        if (!newSpecs[category]) {
          newSpecs[category] = {};
        }
        newSpecs[category][newKey] = newValue;
        setSpecifications(newSpecs);
      }
    }
  };

  // Handler to remove legacy (non-categorized) specifications
  const removeSpecificationLegacy = (key: string) => {
    const newSpecs = { ...specifications };
    delete newSpecs[key];
    setSpecifications(newSpecs);
  };

  if (!specifications || typeof specifications !== 'object') {
    return <span>Aucune spécification</span>;
  }

  const isCategorized = Object.keys(specifications).some(key =>
    key !== 'sectionTitles' && typeof specifications[key] === 'object' && !Array.isArray(specifications[key])
  );

  const hasSpecifications = isCategorized
    ? Object.entries(specifications).some(([key, items]) => key !== 'sectionTitles' && items && typeof items === 'object' && Object.keys(items).length > 0)
    : Object.keys(specifications).filter(key => key !== 'sectionTitles').length > 0;

  return (
    <div className="space-y-2">
      {hasSpecifications && (
        <div className="flex justify-end mb-2">
          <button
            type="button"
            onClick={clearAllSpecifications}
            className="text-sm text-red-600 hover:text-red-700 flex items-center space-x-1"
          >
            <Trash2 className="h-4 w-4" />
            <span>Supprimer toutes les spécifications</span>
          </button>
        </div>
      )}
      
      {!isCategorized ? (
        <div className="space-y-2">
          {Object.entries(specifications).map(([key, value]) => {
             if (key === 'sectionTitles') return null; // Don't show sectionTitles as a spec
             return (
              <div key={key} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="font-medium">{key}:</span>
                <span>{String(value)}</span>
                <button
                  type="button"
                  onClick={() => removeSpecificationLegacy(key)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
             )
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {Object.entries(specifications).map(([category, items]) => {
            if (category === 'sectionTitles') return null; // Don't render sectionTitles as a category

            const isExpanded = expandedCategories[category] !== false;
            const hasItems = items && typeof items === 'object' && Object.keys(items).length > 0;
            const displayTitle = sectionTitles[category] || category;
            
            return (
              <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleCategoryExpansion(category)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    {editingSectionTitle === category ? (
                      <input
                        type="text"
                        value={tempSectionTitle}
                        onChange={(e) => setTempSectionTitle(e.target.value)}
                        onKeyDown={handleSectionTitleKeyDown}
                        className="font-medium text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 mr-2"
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    ) : (
                      <span className="font-medium text-gray-900">{displayTitle}</span>
                    )}
                    {editingSectionTitle === category ? (
                      <div className="flex ml-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={handleSaveSectionTitle}
                          className="text-green-600 hover:text-green-700 mr-2"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEditSectionTitle}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSectionTitle(category);
                        }}
                        className="text-blue-600 hover:text-blue-700 ml-2"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <span className="text-gray-500">
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </span>
                </button>
                
                {isExpanded && (
                  <div className="p-3 bg-white space-y-2">
                    {hasItems ? (
                      Object.entries(items as Record<string, any>).map(([key, value]) => {
                        const isEditing = editingSpec && editingSpec.category === category && editingSpec.key === key;
                        
                        return isEditing ? (
                          // ## EDITING STATE ##
                          <div key={key} className="grid grid-cols-12 gap-2 items-center py-1 bg-blue-50 p-2 rounded">
                            {/* Key Input */}
                            <input
                              type="text"
                              value={tempSpecKey}
                              onChange={(e) => setTempSpecKey(e.target.value)}
                              className="text-sm text-gray-900 border border-blue-300 rounded px-2 py-1 col-span-4"
                            />
                            {/* Value Input */}
                            <input
                              type="text"
                              value={tempSpecValue}
                              onChange={(e) => setTempSpecValue(e.target.value)}
                              className="text-sm text-gray-900 border border-blue-300 rounded px-2 py-1 col-span-6"
                            />
                            {/* Actions */}
                            <div className="col-span-2 flex justify-end space-x-1">
                              <button
                                type="button"
                                onClick={handleSpecSave}
                                className="p-1 text-green-600 hover:bg-green-100 rounded"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={handleSpecCancel}
                                className="p-1 text-red-600 hover:bg-red-100 rounded"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          // ## DISPLAY STATE ##
                          <div key={key} className="grid grid-cols-12 gap-2 items-center py-1 group">
                            {/* Key Display */}
                            <span className="text-sm text-gray-700 col-span-4 truncate" title={key}>{key}:</span>
                            {/* Value Display */}
                            <span className="text-sm text-gray-900 col-span-6 truncate" title={String(value)}>{String(value)}</span>
                            {/* Actions */}
                            <div className="col-span-2 flex justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={() => handleSpecEdit(category, key, String(value))}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSpecDelete(category, key)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-2">Aucun champ dans cette catégorie.</p>
                    )}
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => handleSpecAdd(category)}
                        className="inline-flex items-center px-2 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                      >
                        <Plus className="h-4 w-4 mr-1" /> Ajouter un champ
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ## ----------------------------------------------------------------
// ## Main CarModelsManagement Component
// ## ----------------------------------------------------------------
export function CarModelsManagement() {
  const [models, setModels] = useState<CarModelWithBrand[]>([]);
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingModel, setEditingModel] = useState<CarModelWithBrand | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Add this state for version management
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [selectedModelForVersions, setSelectedModelForVersions] = useState<CarModelWithBrand | null>(null);
  const [versions, setVersions] = useState<CarModelVersion[]>([]);
  const [editingVersion, setEditingVersion] = useState<CarModelVersion | null>(null);
    
  // Add this state for version form
  const [showVersionFormModal, setShowVersionFormModal] = useState(false);
  const [versionFormData, setVersionFormData] = useState({
    name: '',
    description: '',
    price: '',
    engine: '',
    transmission: '',
    power: '',
    fuel_type: '',
    carrosserie: '',
    garantie: '',
    nombre_de_places: '',
    images: [] as string[],
    features: [] as string[],
    specifications: {} as Record<string, any>,
    is_active: true
  });

  // Add state for characteristics import modal
  const [showCharacteristicsModal, setShowCharacteristicsModal] = useState(false);
  const [characteristicsText, setCharacteristicsText] = useState('');
  const [parsedCharacteristics, setParsedCharacteristics] = useState<ParsedCharacteristic[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [editingCharacteristic, setEditingCharacteristic] = useState<ParsedCharacteristic | null>(null);
  const [tempCharacteristicKey, setTempCharacteristicKey] = useState('');
  const [tempCharacteristicDisplayKey, setTempCharacteristicDisplayKey] = useState('');
  const [tempCharacteristicValue, setTempCharacteristicValue] = useState('');
    
  // State for expanded categories in specifications
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  // Add state for editable section titles
  const [sectionTitles, setSectionTitles] = useState<Record<string, string>>({
    'Carrosserie': 'Carrosserie',
    'Dimensions': 'Dimensions',
    'Motorisation': 'Motorisation',
    'Transmission': 'Transmission',
    'Performances': 'Performances',
    'Consommation': 'Consommation',
    'Equipements de sécurité': 'Equipements de sécurité',
    'Aides à la conduite': 'Aides à la conduite',
    'Audio et communication': 'Audio et communication',
    'Équipements': 'Équipements'
  });

  // Add state for editing section titles
  const [editingSectionTitle, setEditingSectionTitle] = useState<string | null>(null);
  const [tempSectionTitle, setTempSectionTitle] = useState('');

  const [formData, setFormData] = useState({
    brand_id: '',
    name: '',
    description: '',
    starting_price: '',
    engine: '',
    transmission: '',
    power: '',
    fuel_type: '',
    carrosserie: '',
    garantie: '', 
    nombre_de_places: '',
    images: [] as string[],
    features: [] as string[],
    specifications: {} as Record<string, any>,
    is_active: true
  });

  // Filter states for models
  const [modelSearchTerm, setModelSearchTerm] = useState('');
  const [selectedBrandFilter, setSelectedBrandFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showModelFilters, setShowModelFilters] = useState(false);
  
  // NEW SORT STATE
  const [sortOrder, setSortOrder] = useState<'price_asc' | 'recent'>('price_asc');

  // Filter states for versions
  const [versionSearchTerm, setVersionSearchTerm] = useState('');
  const [selectedVersionStatusFilter, setSelectedVersionStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [priceRangeFilter, setPriceRangeFilter] = useState({ min: '', max: '' });
  const [showVersionFilters, setShowVersionFilters] = useState(false);

  // Options for carrosserie (body type)
  const carrosserieOptions = [
    'Berline',
    'SUV',
    'Hatchback',
    'Break',
    'Coupé',
    'Cabriolet',
    'Monospace',
    'Pickup',
    'Utilitaire',
    'Citadine',
    'Compact',
    'Autre'
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [modelsData, brandsData] = await Promise.all([
        carPricesApi.getModels(),
        carPricesApi.getBrands()
      ]);
      
      setModels(modelsData);
      setBrands(brandsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };
// Add this function near the top of your CarModelsManagement component
const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with a single one
    .trim(); // Remove leading/trailing spaces and hyphens
};
  // Fonction utilitaire pour normaliser une clé de caractéristique (pour la base de données)
  const normalizeKey = useCallback((key: string): string => {
    return key
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  }, []);

  // Fonction utilitaire pour formater une clé DB pour l'affichage
  const formatKeyForDisplay = useCallback((key: string): string => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }, []);

  // Define the specific fields we want to extract
  const targetFields = [
    'Caractéristiques', 'Financement', 'Disponibilité', 'Carrosserie', 'Garantie',  
    'Nombre de places', 'Nombre de portes', 'Motorisation',  
    'Nombre de cylindres', 'Nombre de moteurs électriques', 'Energie', 'Puissance fiscale',  
    'Puissance (ch.din)', 'Puissance thermique (ch.din)', 'Puissance avec overboost (ch.din)',  
    'Puissance électrique', 'Batterie', 'Couple', 'Couple électrique', 'Cylindrée',  
    'Technologie hybride', 'Transmission', 'Boîte', 'Nombre de rapports', 'Transmission',  
    '4 roues directrices', 'Différentiel autobloquant', 'Dimensions', 'Longueur',  
    'Largeur', 'Hauteur', 'Charge utile', 'Volume du coffre', 'Volume du coffre maximal',  
    'Volume du coffre avant', 'Volume utile', 'Surface de chargement', 'Performances',  
    '0-100 Km/h', 'Vitesse maxi', 'Autonomie électrique (WLTP)', 'Autonomie électrique (NEDC)',  
    'Consommation', 'Consommation urbaine', 'Consommation extra-urbaine', 'Consommation mixte',  
    'Consommation électrique (WLTP)', 'Capacité de recharge maximale (AC)',  
    'Temps de recharge normale (AC)', 'Emissions de CO2', 'Temps de recharge rapide (DC)',  
    'Equipements de sécurité', 'ABS', 'Airbags', 'Alarme antivol', 'Alerte de franchissement de ligne',  
    'Alerte de risque de collision', 'Allumage automatique des feux', 'Anti-démarrage électronique',  
    'Anti-patinage', 'Assistance au freinage', 'Barre stabilisatrice', 'Capot moteur actif',  
    'Contrôle adaptatif de la charge', 'Contrôle de pression des pneus', 'DSC', 'Fixations ISOFIX',  
    'Kit sécurité', 'Stabilisateur de vent latéral', 'Aides à la conduite', 'Aide au démarrage en côte',  
    'Aide au maintien dans la voie', 'Aide au stationnement', 'Alerte de circulation transversale',  
    'Assistant de feux de route', 'Caméra d\'angle mort', 'BMW Night Vision', 'Caméra d\'intersection',  
    'Conduite autonome', 'Contrôle de stabilité anti-retournement', 'Contrôle du roulis de la remorque',  
    'Détecteur de fatigue', 'Détection des piétons', 'Direction assistée', 'Parkassist',  
    'Limiteur de vitesse', 'Régulateur de vitesse', 'Reconnaissance des panneaux',  
    'Surveillance d\'angle mort', 'Système de descente en côte', 'Équipements',  
    'Aileron arrière', 'Airbumps', 'Antibrouillards', 'Antenne Antivol', 'Arceau de benne',  
    'Baguettes extérieures d\'encadrement des vitres', 'Calandre', 'Barres de toit',  
    'Boulons de roues protégés', 'Capote éléctrique', 'Eléments extérieurs couleur carrosserie',  
    'Feux à LED', 'Jantes', 'Pneumatiques', 'Jupes latérales', 'Kit carrosserie', 'Lave Phares',  
    'Marchepieds latéraux', 'Pack aérodynamique M', 'Peinture', 'Phares', 'Pneumatiques runflat',  
    'Poignées de portes', 'Rideau de benne', 'Sideblades', 'Sorties d\'échappement',  
    'Spoiler arrière', 'Porte bagages', 'Audio et communication', 'Autoradio', 'Connectivité',  
    'Ecran central', 'Navigation', 'Connected Drive', 'Accoudoirs',  
    'Appuis têtes arrières', 'Boîte à gants réfrigérée', 'Ciel de pavillon',  
    'Cloison entre cabine et compartiment arrière', 'Finition intérieure', 'Kit fumeurs',  
    'Kit éclairage', 'Kit rangement', 'Lumières d\'ambiance', 'Pack nuit', 'Pare-brise panoramique Zenith',  
    'Pédaliers', 'Pommeau de levier de vitesse', 'Sellerie', 'Seuils de portes', 'Sièges',  
    'Stores pare-soleil', 'Sièges réglables en largeur', 'Sièges réglables en hauteur',  
    'Tapis de sol', 'Tableau de bord', 'Toit', 'Vitres', 'Volant', 'Volant réglable',  
    'Affichage tête haute', 'Alimentation électrique', 'Audi Drive Select',  
    'Boule d\'attelage', 'Bouton Start & Stop', 'Caméra d\'enregistrement de conduite',  
    'Carte mains libres', 'ConnectedCAM', 'Clé intelligente', 'Coffre à bagages', 'Climatisation',  
    'Détecteur de pluie', 'Dynamic Response', 'Electronic Line-Lock', 'Éclairage d\'accompagnement',  
    'Freinage', 'Fermeture assistée des portes', 'Fermeture centralisée', 'Frein de stationnement',  
    'Modes de conduite', 'Ordinateur de bord', 'Portes en élytre', 'Rétroviseur intérieur',  
    'Rétroviseurs extérieurs', 'Préparation pour téléphone', 'Système Start & Stop',  
    'Système de caméra 360', 'Terrain Response', 'Système i-Stop', 'Servotronic', 'Magic Wash',  
    'SYNC 2', 'Train roulant', 'Pack entretien', 'Suspension', 'Système de récupération d\'énergie',  
    'Stationnement télécommandé', 'Instrumentation digitale', 'Launch Control', 'Pack Sport Chrono',  
    'SYNC', 'Palettes au volant', 'Version pays chauds', 'Vitres électriques'
  ];

  // Define category mappings for organizing the data
  const categoryMappings: Record<string, string[]> = {
    'Carrosserie': ['Carrosserie', 'Type', 'Garantie', 'Nombre de places', 'Nombre de portes'],
    'Motorisation': ['Motorisation', 'Nombre de cylindres', 'Nombre de moteurs électriques', 'Energie', 'Puissance fiscale',  
                     'Puissance (ch.din)', 'Puissance thermique (ch.din)', 'Puissance avec overboost (ch.din)',  
                     'Puissance électrique', 'Batterie', 'Couple', 'Couple électrique', 'Cylindrée', 'Technologie hybride'],
    'Transmission': ['Transmission', 'Boîte', 'Nombre de rapports', '4 roues directrices', 'Différentiel autobloquant'],
    'Dimensions': ['Dimensions', 'Longueur', 'Largeur', 'Hauteur', 'Charge utile', 'Volume du coffre',  
                   'Volume du coffre maximal', 'Volume du coffre avant', 'Volume utile', 'Surface de chargement'],
    'Performances': ['Performances', '0-100 Km/h', 'Vitesse maxi', 'Autonomie électrique (WLTP)', 'Autonomie électrique (NEDC)'],
    'Consommation': ['Consommation', 'Consommation urbaine', 'Consommation extra-urbaine', 'Consommation mixte',  
                     'Consommation électrique (WLTP)', 'Capacité de recharge maximale (AC)',  
                     'Temps de recharge normale (AC)', 'Emissions de CO2', 'Temps de recharge rapide (DC)'],
    'Equipements de sécurité': ['Equipements de sécurité', 'ABS', 'Airbags', 'Alarme antivol', 'Alerte de franchissement de ligne',  
                              'Alerte de risque de collision', 'Allumage automatique des feux', 'Anti-démarrage électronique',  
                              'Anti-patinage', 'Assistance au freinage', 'Barre stabilisatrice', 'Capot moteur actif',  
                              'Contrôle adaptatif de la charge', 'Contrôle de pression des pneus', 'DSC', 'Fixations ISOFIX',  
                              'Kit sécurité', 'Stabilisateur de vent latéral'],
    'Aides à la conduite': ['Aides à la conduite', 'Aide au démarrage en côte', 'Aide au maintien dans la voie',  
                           'Aide au stationnement', 'Alerte de circulation transversale', 'Assistant de feux de route',  
                           'Caméra d\'angle mort', 'BMW Night Vision', 'Caméra d\'intersection', 'Conduite autonome',  
                           'Contrôle de stabilité anti-retournement', 'Contrôle du roulis de la remorque',  
                           'Détecteur de fatigue', 'Détection des piétons', 'Direction assistée', 'Parkassist',  
                           'Limiteur de vitesse', 'Régulateur de vitesse', 'Reconnaissance des panneaux',  
                           'Surveillance d\'angle mort', 'Système de descente en côte'],
    'Audio et communication': ['Audio et communication', 'Autoradio', 'Connectivité', 'Ecran central', 'Navigation', 'Connected Drive'],
    'Équipements': [
      // Exterior equipment
      'Aileron arrière', 'Airbumps', 'Antibrouillards', 'Antenne Antivol', 'Arceau de benne',  
      'Baguettes extérieures d\'encadrement des vitres', 'Calandre', 'Barres de toit',  
      'Boulons de roues protégés', 'Capote éléctrique', 'Eléments extérieurs couleur carrosserie',  
      'Feux à LED', 'Jantes', 'Pneumatiques', 'Jupes latérales', 'Kit carrosserie', 'Lave Phares',  
      'Marchepieds latéraux', 'Pack aérodynamique M', 'Peinture', 'Phares', 'Pneumatiques runflat',  
      'Poignées de portes', 'Rideau de benne', 'Sideblades', 'Sorties d\'échappement',  
      'Spoiler arrière', 'Porte bagages',
      // Interior equipment
      'Accoudoirs', 'Appuis têtes arrières', 'Boîte à gants réfrigérée', 'Ciel de pavillon',  
      'Cloison entre cabine et compartiment arrière', 'Finition intérieure', 'Kit fumeurs',  
      'Kit éclairage', 'Kit rangement', 'Lumières d\'ambiance', 'Pack nuit', 'Pare-brise panoramique Zenith',  
      'Pédaliers', 'Pommeau de levier de vitesse', 'Sellerie', 'Seuils de portes', 'Sièges',  
      'Stores pare-soleil', 'Sièges réglables en largeur', 'Sièges réglables en hauteur',  
      'Tapis de sol', 'Tableau de bord', 'Toit', 'Vitres', 'Volant', 'Volant réglable',
      // Functional equipment
      'Affichage tête haute', 'Alimentation électrique', 'Audi Drive Select',  
      'Boule d\'attelage', 'Bouton Start & Stop', 'Caméra d\'enregistrement de conduite',  
      'Carte mains libres', 'ConnectedCAM', 'Clé intelligente', 'Coffre à bagages', 'Climatisation',  
      'Détecteur de pluie', 'Dynamic Response', 'Electronic Line-Lock', 'Éclairage d\'accompagnement',  
      'Freinage', 'Fermeture assistée des portes', 'Fermeture centralisée', 'Frein de stationnement',  
      'Modes de conduite', 'Ordinateur de bord', 'Portes en élytre', 'Rétroviseur intérieur',  
      'Rétroviseurs extérieurs', 'Préparation pour téléphone', 'Système Start & Stop',  
      'Système de caméra 360', 'Terrain Response', 'Système i-Stop', 'Servotronic', 'Magic Wash',  
      'SYNC 2', 'Train roulant', 'Pack entretien', 'Suspension', 'Système de récupération d\'énergie',  
      'Stationnement télécommandé', 'Instrumentation digitale', 'Launch Control', 'Pack Sport Chrono',  
      'SYNC', 'Palettes au volant', 'Version pays chauds', 'Vitres électriques'
    ]
  };

  // Function to truncate text to a maximum length
  const truncateText = useCallback((text: string, maxLength: number = 50): string => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }, []);

  const getBrandName = useCallback((brandId: string) => {
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : 'Marque inconnue';
  }, [brands]);

  const filteredModels = useMemo(() => {
    let result = models.filter(model => {
      const brandName = getBrandName(model.brand_id);
      const matchesSearch = model.name.toLowerCase().includes(modelSearchTerm.toLowerCase()) ||
                            brandName.toLowerCase().includes(modelSearchTerm.toLowerCase());
      const matchesBrand = !selectedBrandFilter || model.brand_id === selectedBrandFilter;
      const matchesStatus = selectedStatusFilter === 'all' || 
                            (selectedStatusFilter === 'active' && model.is_active) ||
                            (selectedStatusFilter === 'inactive' && !model.is_active);
      
      return matchesSearch && matchesBrand && matchesStatus;
    });

    // Apply sorting
    result.sort((a, b) => {
      if (sortOrder === 'recent') {
        // Sort by created_at descending (newest first)
        // Check if created_at exists on the model type, if not fallback to id or other logic
        const dateA = new Date((a as any).created_at || 0).getTime();
        const dateB = new Date((b as any).created_at || 0).getTime();
        return dateB - dateA;
      } else {
        // Sort by price ascending (lowest to highest)
        // Treat null prices as highest so they go to bottom
        const priceA = a.starting_price !== null ? a.starting_price : Infinity;
        const priceB = b.starting_price !== null ? b.starting_price : Infinity;
        return priceA - priceB;
      }
    });

    return result;
  }, [models, modelSearchTerm, selectedBrandFilter, selectedStatusFilter, getBrandName, sortOrder]);

  const filteredVersions = useMemo(() => {
    return versions.filter(version => {
      const matchesSearch = version.name.toLowerCase().includes(versionSearchTerm.toLowerCase());
      const matchesStatus = selectedVersionStatusFilter === 'all' || 
                            (selectedVersionStatusFilter === 'active' && version.is_active) ||
                            (selectedVersionStatusFilter === 'inactive' && !version.is_active);
      const matchesPriceMin = !priceRangeFilter.min || (version.price && version.price >= parseFloat(priceRangeFilter.min));
      const matchesPriceMax = !priceRangeFilter.max || (version.price && version.price <= parseFloat(priceRangeFilter.max));
      
      return matchesSearch && matchesStatus && matchesPriceMin && matchesPriceMax;
    });
  }, [versions, versionSearchTerm, selectedVersionStatusFilter, priceRangeFilter]);

  const processSpecificationsForApi = useCallback((specifications: Record<string, any>): Record<string, any> => {
    const processed: Record<string, any> = {};
    
    // First, include section titles in the processed data
    processed.sectionTitles = sectionTitles;
    
    Object.entries(specifications).forEach(([category, items]) => {
      if (category === 'sectionTitles') return; // Already handled

      if (typeof items === 'object' && !Array.isArray(items)) {
        const processedCategory: Record<string, any> = {};
        
        Object.entries(items).forEach(([key, value]) => {
          // We save the display key (e.g., "Hauteur (m)") directly
          let processedValue = String(value);
          processedCategory[key] = truncateText(processedValue, 255);
        });
        
        processed[category] = processedCategory;
      } else {
        // This case handles old/non-categorized data
        let processedValue = String(items);
        processed[normalizeKey(category)] = truncateText(processedValue, 255);
      }
    });
    
    return processed;
  }, [truncateText, normalizeKey, sectionTitles]);
  
  const organizeDataIntoCategories = useCallback((data: Record<string, any>) => {
    const organizedData: Record<string, Record<string, any>> = {};
    
    // First, check if sectionTitles are in the data and update the state if found
    // This is important when loading data *from* the DB
    if (data.sectionTitles) {
      setSectionTitles(prevTitles => ({ ...prevTitles, ...data.sectionTitles }));
    }
    
    Object.keys(categoryMappings).forEach(category => {
      organizedData[category] = {};
    });
    
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'sectionTitles') return;

      let foundCategory = null;
      const readableKey = formatKeyForDisplay(key); // For matching against mapping

      // Check if data is already categorized
      if (categoryMappings[key] && typeof value === 'object' && !Array.isArray(value)) {
         organizedData[key] = { ...organizedData[key], ...value };
      } else {
        // Find category for flat data
        for (const [category, fields] of Object.entries(categoryMappings)) {
          if (fields.some(field => normalizeKey(field) === key)) {
            foundCategory = category;
            break;
          }
        }
        
        if (foundCategory) {
          // Use the human-readable key for display
          organizedData[foundCategory][readableKey] = value;
        }
      }
    });

    return organizedData;
  }, [categoryMappings, normalizeKey, formatKeyForDisplay]);

  // Function to format specifications into the exact required format
  const formatSpecificationsForDisplay = useCallback((specifications: Record<string, any>): string => {
    if (!specifications) return '';
    
    // Helper to get value from specifications
    const getValue = (category: string, field: string): string => {
      if (!specifications[category]) return '';
      
      const categoryData = specifications[category];
      
      // Try exact match first (for data that is already well-formed)
      if (categoryData[field] !== undefined) {
        return String(categoryData[field] || '');
      }
      
      // Try normalized match (for imported data)
      const normalizedField = normalizeKey(field);
      for (const [key, value] of Object.entries(categoryData)) {
        if (normalizeKey(key) === normalizedField) {
          return String(value || '');
        }
      }
      
      return '';
    };

    // Helper to get all equipment values
    const getEquipmentValues = (categoryName: string): string[] => {
      const equipmentValues: string[] = [];
      const categoryTitle = sectionTitles[categoryName] || categoryName;
      
      if (specifications[categoryTitle]) {
        const equipmentData = specifications[categoryTitle];
        
        for (const [key, value] of Object.entries(equipmentData)) {
          if (value && String(value).toLowerCase() !== 'true' && String(value).toLowerCase() !== 'oui') {
            equipmentValues.push(`${key}: ${value}`);
          } else {
            equipmentValues.push(key);
          }
        }
      }
      
      return equipmentValues;
    };

    // Start building the formatted output
    let formattedOutput = 'Fiche technique\n\n';
    
    // Carrosserie section
    const carrosserieTitle = sectionTitles['Carrosserie'] || 'Carrosserie';
    formattedOutput += `${carrosserieTitle}\n`;
    formattedOutput += `Type: ${getValue('Carrosserie', 'Carrosserie') || getValue('Carrosserie', 'Type')}\n`;
    formattedOutput += `Garantie: ${getValue('Carrosserie', 'Garantie')}\n`;
    formattedOutput += `Nombre de places: ${getValue('Carrosserie', 'Nombre de places')}\n`;
    formattedOutput += `Nombre de portes: ${getValue('Carrosserie', 'Nombre de portes')}\n\n`;
    
    // Dimensions section
    const dimensionsTitle = sectionTitles['Dimensions'] || 'Dimensions';
    formattedOutput += `${dimensionsTitle}\n`;
    formattedOutput += `Longueur: ${getValue('Dimensions', 'Longueur')}\n`;
    formattedOutput += `Largeur: ${getValue('Dimensions', 'Largeur')}\n`;
    formattedOutput += `Hauteur: ${getValue('Dimensions', 'Hauteur')}\n`;
    formattedOutput += `Volume du coffre: ${getValue('Dimensions', 'Volume du coffre')}\n`;
    formattedOutput += `Volume du coffre maximal: ${getValue('Dimensions', 'Volume du coffre maximal')}\n\n`;
    
    // Motorisation section
    const motorisationTitle = sectionTitles['Motorisation'] || 'Motorisation';
    formattedOutput += `${motorisationTitle}\n`;
    formattedOutput += `Nombre de cylindres: ${getValue('Motorisation', 'Nombre de cylindres')}\n`;
    formattedOutput += `Energie: ${getValue('Motorisation', 'Energie')}\n`;
    formattedOutput += `Puissance fiscale: ${getValue('Motorisation', 'Puissance fiscale')}\n`;
    
    // Handle different power fields
    let powerValue = getValue('Motorisation', 'Puissance (ch.din)');
    if (!powerValue) {
      powerValue = getValue('Motorisation', 'Puissance thermique (ch.din)');
    }
    if (!powerValue) {
      powerValue = getValue('Motorisation', 'Puissance avec overboost (ch.din)');
    }
    formattedOutput += `Puissance réelle (ch.din): ${powerValue}\n`;
    
    formattedOutput += `Couple: ${getValue('Motorisation', 'Couple')}\n`;
    formattedOutput += `Cylindrée: ${getValue('Motorisation', 'Cylindrée')}\n\n`;
    
    // Transmission section
    const transmissionTitle = sectionTitles['Transmission'] || 'Transmission';
    formattedOutput += `${transmissionTitle}\n`;
    formattedOutput += `Boîte: ${getValue('Transmission', 'Boîte')}\n`;
    formattedOutput += `Nombre de rapports: ${getValue('Transmission', 'Nombre de rapports')}\n`;
    formattedOutput += `Transmission: ${getValue('Transmission', 'Transmission')}\n\n`;
    
    // Performances section
    const performancesTitle = sectionTitles['Performances'] || 'Performances';
    formattedOutput += `${performancesTitle}\n`;
    formattedOutput += `0-100 Km/h: ${getValue('Performances', '0-100 Km/h')}\n`;
    formattedOutput += `Vitesse maxi: ${getValue('Performances', 'Vitesse maxi')}\n\n`;
    
    // Consommation section
    const consommationTitle = sectionTitles['Consommation'] || 'Consommation';
    formattedOutput += `${consommationTitle}\n`;
    formattedOutput += `Consommation urbaine: ${getValue('Consommation', 'Consommation urbaine')}\n`;
    formattedOutput += `Consommation extra-urbaine: ${getValue('Consommation', 'Consommation extra-urbaine')}\n`;
    formattedOutput += `Consommation mixte: ${getValue('Consommation', 'Consommation mixte')}\n\n`;
    
    // Equipements de sécurité
    const securiteTitle = sectionTitles['Equipements de sécurité'] || 'Equipements de sécurité';
    formattedOutput += `${securiteTitle}\n`;
    const securiteValues = getEquipmentValues('Equipements de sécurité');
    if (securiteValues.length > 0) {
      formattedOutput += securiteValues.join('\n') + '\n\n';
    } else {
      formattedOutput += 'N/A\n\n';
    }

    // Aides à la conduite section
    const aidesTitle = sectionTitles['Aides à la conduite'] || 'Aides à la conduite';
    formattedOutput += `${aidesTitle}\n`;
    const aidesValues = getEquipmentValues('Aides à la conduite');
     if (aidesValues.length > 0) {
      formattedOutput += aidesValues.join('\n') + '\n\n';
    } else {
      formattedOutput += 'N/A\n\n';
    }
    
    // Audio et communication section
    const audioTitle = sectionTitles['Audio et communication'] || 'Audio et communication';
    formattedOutput += `${audioTitle}\n`;
    const audioValues = getEquipmentValues('Audio et communication');
     if (audioValues.length > 0) {
      formattedOutput += audioValues.join('\n') + '\n\n';
    } else {
      formattedOutput += 'N/A\n\n';
    }

    // Équipements section
    const equipementsTitle = sectionTitles['Équipements'] || 'Équipements';
    formattedOutput += `${equipementsTitle}\n`;
    const equipmentValues = getEquipmentValues('Équipements');
    if (equipmentValues.length > 0) {
      formattedOutput += equipmentValues.join('\n') + '\n\n';
    } else {
      formattedOutput += 'N/A\n\n';
    }
    
    return formattedOutput;
  }, [normalizeKey, sectionTitles]);

  const parseCharacteristics = useCallback(() => {
    try {
      setParseError(null);
      const text = characteristicsText.replace(/\r/g, '').trim();
      const result: ParsedCharacteristic[] = [];
      
      if (!text) {
        setParseError("Le champ de caractéristiques est vide.");
        return;
      }

      const displayKeysMap: Record<string, string> = {};
      targetFields.forEach(field => {
        const dbKey = normalizeKey(field);
        displayKeysMap[dbKey] = field;
      });

      const isSeparated = text.includes('\t') || text.includes(':') || text.includes('\n');
      
      if (isSeparated) {
        const lines = text.split('\n');
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) continue;

          const separator = trimmedLine.includes('\t') ? '\t' : (trimmedLine.includes(':') ? ':' : null);
          
          if (separator) {
            const parts = trimmedLine.split(separator);
            const key = parts[0].trim();
            const value = parts.slice(1).join(separator).trim();
            
            if (key && value) {
              const dbKey = normalizeKey(key);
              const finalDisplayKey = displayKeysMap[dbKey] || key;

              result.push({
                  key: dbKey,
                  displayKey: finalDisplayKey,
                  value: value
              });
            }
          }
        }
      } 
      
      if (result.length === 0) {
        const sortedKeys = [...targetFields].sort((a, b) => b.length - a.length);
        const allKeysRegex = sortedKeys.map(key => key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')).join('|');
        const finalRegex = new RegExp(`(${allKeysRegex})`, 'g');

        const keyOccurrences: { key: string; index: number; }[] = [];
        
        let cleanText = text.replace(/[\s\n]+/g, '');  
        let m;
        while ((m = finalRegex.exec(cleanText)) !== null) {
          const isOverlapped = keyOccurrences.some(occ => {
              return m!.index >= occ.index && m!.index + m![0].length <= occ.index + occ.key.length;
          });
          
          if (!isOverlapped) {
              keyOccurrences.push({ key: m[0], index: m.index });
          }
        }
        
        for (let i = 0; i < keyOccurrences.length; i++) {
          const currentKey = keyOccurrences[i].key;
          const startIndex = keyOccurrences[i].index + currentKey.length;
          const endIndex = i < keyOccurrences.length - 1 
              ? keyOccurrences[i + 1].index 
              : cleanText.length;

          let value = cleanText.substring(startIndex, endIndex).trim();
          
          const dbKey = normalizeKey(currentKey);

          result.push({
              key: dbKey,
              displayKey: currentKey,
              value: value
          });
        }

        if (result.length === 0 && !isSeparated) {
           setParseError("Aucune caractéristique n'a pu être extraite. Le format ne correspond pas aux clés de caractéristiques attendues.");
           return;
        }
      }
      
      if (result.length === 0) {
        setParseError("Aucune caractéristique n'a pu être extraite en utilisant les séparateurs (tab, :) ou en analysant le texte compact.");
        return;
      }
      
      setParsedCharacteristics(result);
    } catch (error) {
      console.error('Error parsing characteristics:', error);
      setParseError("Une erreur s'est produite lors de l'analyse des caractéristiques. Veuillez vérifier le format et réessayez.");
    }
  }, [characteristicsText, normalizeKey, targetFields]);

  // Add this function to ensure all categories exist when loading specifications
  const ensureAllCategoriesExist = (specifications: Record<string, any>): Record<string, any> => {
    const result = { ...specifications };
    
    // Ensure all defined categories exist, even if empty
    Object.keys(categoryMappings).forEach(category => {
      if (!result[category]) {
        result[category] = {};
      }
    });
    
    return result;
  };

  // Update the loadSpecifications function to use this
  const loadSpecifications = (specs: Record<string, any> | null | undefined): Record<string, any> => {
    let loadedSpecs = specs ? JSON.parse(JSON.stringify(specs)) : {}; // Deep copy to avoid mutation
    
    // Load section titles from the specifications if available
    if (loadedSpecs && loadedSpecs.sectionTitles) {
      // Merge titles, giving precedence to loaded titles
      const mergedTitles = { ...sectionTitles, ...loadedSpecs.sectionTitles };
      setSectionTitles(mergedTitles);
      loadedSpecs.sectionTitles = mergedTitles;
    } else {
      // Ensure sectionTitles key exists if not present
      loadedSpecs.sectionTitles = sectionTitles;
    }
    
    // Ensure all categories exist
    loadedSpecs = ensureAllCategoriesExist(loadedSpecs);
    
    // Check if data is old (flat) or new (categorized)
    const isCategorized = Object.keys(loadedSpecs).some(key =>
        key !== 'sectionTitles' && typeof loadedSpecs[key] === 'object'
    );

    if (!isCategorized && Object.keys(loadedSpecs).length > 1) { // >1 to exclude just sectionTitles
        // Data is flat, need to organize it
        loadedSpecs = organizeDataIntoCategories(loadedSpecs);
    }

    return loadedSpecs;
  };

  const clearAllSpecifications = useCallback(() => {
    if (confirm('Êtes-vous sûr de vouloir supprimer toutes les spécifications techniques ?')) {
      // Create empty categories
      const emptySpecs: Record<string, any> = {
        sectionTitles: sectionTitles // Preserve titles
      };
      Object.keys(categoryMappings).forEach(category => {
        emptySpecs[category] = {};
      });

      if (showVersionFormModal) {
        setVersionFormData(prev => ({
          ...prev,
          specifications: emptySpecs
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          specifications: emptySpecs
        }));
      }
    }
  }, [showVersionFormModal, sectionTitles, categoryMappings]);

  const replaceAllSpecifications = useCallback(() => {
    setShowCharacteristicsModal(true);
    setParsedCharacteristics([]);
  }, []);

  const handleApplyCharacteristics = useCallback(() => {
    const isVersionForm = showVersionFormModal;
    
    const characteristicsMap: Record<string, any> = {};

    parsedCharacteristics.forEach(item => {
      // Use the displayKey as the key in the map for categorization
      characteristicsMap[item.displayKey] = item.value;
    });
    
    // Re-organize based on display key
    const organizedData: Record<string, Record<string, any>> = {};
    Object.keys(categoryMappings).forEach(category => {
      organizedData[category] = {};
    });
    
    Object.entries(characteristicsMap).forEach(([key, value]) => {
      let foundCategory = null;
      for (const [category, fields] of Object.entries(categoryMappings)) {
        // Match against displayKey (key)
        if (fields.includes(key)) {
          foundCategory = category;
          break;
        }
      }
      
      if (foundCategory) {
        organizedData[foundCategory][key] = value;
      } else {
         // Fallback for keys not in mappings (e.g. 'Équipements')
         if (key.toLowerCase().includes('equipement')) {
           organizedData['Équipements'][key] = value;
         }
      }
    });

    if (isVersionForm) {
      const updatedVersionFormData = { ...versionFormData };
      
      // Update top-level fields
      if (characteristicsMap['Carrosserie']) {
        updatedVersionFormData.carrosserie = truncateText(String(characteristicsMap['Carrosserie']), 50);
      }
      if (characteristicsMap['Garantie']) {
        updatedVersionFormData.garantie = truncateText(String(characteristicsMap['Garantie']), 10);
      }
      if (characteristicsMap['Nombre de places']) {
        updatedVersionFormData.nombre_de_places = truncateText(String(characteristicsMap['Nombre de places']), 10);
      }
      if (characteristicsMap['Motorisation']) {
        updatedVersionFormData.engine = truncateText(String(characteristicsMap['Motorisation']), 53);
      }
      if (characteristicsMap['Energie']) {
        updatedVersionFormData.fuel_type = truncateText(String(characteristicsMap['Energie']), 50);
      }
      if (characteristicsMap['Puissance (ch.din)']) {
        updatedVersionFormData.power = truncateText(String(characteristicsMap['Puissance (ch.din)']), 50);
      }
      if (characteristicsMap['Boîte']) {
        updatedVersionFormData.transmission = truncateText(String(characteristicsMap['Boîte']), 50);
      }
      
      updatedVersionFormData.specifications = organizedData;
      
      const features = [...versionFormData.features];
      
      Object.keys(characteristicsMap).forEach(key => {
          if (key.includes('equipements') || key.includes('aides') || key.includes('audio') || key.includes('securite')) {
              const newFeatures = String(characteristicsMap[key]).split('|').map((f: string) => truncateText(f.trim(), 100));
              features.push(...newFeatures);
          }
      });
      
      updatedVersionFormData.features = [...new Set(features)].filter(f => f);
      
      setVersionFormData(updatedVersionFormData);
    } else {
      const updatedFormData = { ...formData };
      
      if (characteristicsMap['Carrosserie']) {
        updatedFormData.carrosserie = truncateText(String(characteristicsMap['Carrosserie']), 50);
      }
      if (characteristicsMap['Garantie']) {
        updatedFormData.garantie = truncateText(String(characteristicsMap['Garantie']), 10);
      }
      if (characteristicsMap['Nombre de places']) {
        updatedFormData.nombre_de_places = truncateText(String(characteristicsMap['Nombre de places']), 10);
      }
      if (characteristicsMap['Motorisation']) {
        updatedFormData.engine = truncateText(String(characteristicsMap['Motorisation']), 50);
      }
      if (characteristicsMap['Energie']) {
        updatedFormData.fuel_type = truncateText(String(characteristicsMap['Energie']), 50);
      }
      if (characteristicsMap['Puissance (ch.din)']) {
        updatedFormData.power = truncateText(String(characteristicsMap['Puissance (ch.din)']), 50);
      }
      if (characteristicsMap['Boîte']) {
        updatedFormData.transmission = truncateText(String(characteristicsMap['Boîte']), 50);
      }
      
      updatedFormData.specifications = organizedData;
      
      const features = [...formData.features];

      Object.keys(characteristicsMap).forEach(key => {
        if (key.includes('equipements') || key.includes('aides') || key.includes('audio') || key.includes('securite')) {
            const newFeatures = String(characteristicsMap[key]).split('|').map((f: string) => truncateText(f.trim(), 100));
            features.push(...newFeatures);
        }
      });
      
      updatedFormData.features = [...new Set(features)].filter(f => f);
      
      setFormData(updatedFormData);
    }
    
    setShowCharacteristicsModal(false);
    setCharacteristicsText('');
    setParsedCharacteristics([]);
    setParseError(null);
  }, [formData, versionFormData, showVersionFormModal, parsedCharacteristics, truncateText, categoryMappings]);

  const handleDeleteCharacteristic = useCallback((key: string, value: string) => {
    const updatedCharacteristics = parsedCharacteristics.filter(c => !(c.key === key && c.value === value));
    setParsedCharacteristics(updatedCharacteristics);
  }, [parsedCharacteristics]);

  const handleAddCharacteristic = useCallback(() => {
    if (tempCharacteristicDisplayKey && tempCharacteristicValue) {
      const newDbKey = normalizeKey(tempCharacteristicDisplayKey);
      
      const newCharacteristic: ParsedCharacteristic = {
        key: newDbKey,
        displayKey: tempCharacteristicDisplayKey,
        value: tempCharacteristicValue
      };

      const updatedCharacteristics = [...parsedCharacteristics, newCharacteristic];
      setParsedCharacteristics(updatedCharacteristics);
      setTempCharacteristicKey('');
      setTempCharacteristicDisplayKey('');
      setTempCharacteristicValue('');
    }
  }, [tempCharacteristicDisplayKey, tempCharacteristicValue, parsedCharacteristics, normalizeKey]);

  const handleEditCharacteristic = useCallback((index: number) => {
    const item = parsedCharacteristics[index];
    if (item) {
      setEditingCharacteristic(item);
      setTempCharacteristicKey(item.key);
      setTempCharacteristicDisplayKey(item.displayKey);
      setTempCharacteristicValue(item.value);
    }
  }, [parsedCharacteristics]);

  const handleSaveEditedCharacteristic = useCallback(() => {
    if (editingCharacteristic) {
      const updatedCharacteristics = [...parsedCharacteristics];
      const index = updatedCharacteristics.findIndex(c => c.key === editingCharacteristic.key && c.value === editingCharacteristic.value);

      if (index !== -1) {
        const newDbKey = normalizeKey(tempCharacteristicDisplayKey);
        
        updatedCharacteristics[index] = {
          key: newDbKey,
          displayKey: tempCharacteristicDisplayKey,
          value: tempCharacteristicValue
        };
        
        setParsedCharacteristics(updatedCharacteristics);
        setEditingCharacteristic(null);
        setTempCharacteristicKey('');
        setTempCharacteristicDisplayKey('');
        setTempCharacteristicValue('');
      } else {
        handleAddCharacteristic();
      }
    }
  }, [editingCharacteristic, parsedCharacteristics, tempCharacteristicDisplayKey, tempCharacteristicValue, normalizeKey, handleAddCharacteristic]);

  const toggleCategoryExpansion = useCallback((category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  // Function to handle section title editing
  const handleEditSectionTitle = useCallback((section: string) => {
    setEditingSectionTitle(section);
    setTempSectionTitle(sectionTitles[section] || section);
  }, [sectionTitles]);

  const handleSaveSectionTitle = useCallback(() => {
    if (editingSectionTitle) {
      const oldTitle = editingSectionTitle;
      const newTitle = tempSectionTitle;
      
      // Update sectionTitles state
      setSectionTitles(prev => {
         const newTitles = { ...prev };
         // Just update the display title, don't change the key
         newTitles[oldTitle] = newTitle;
         return newTitles;
      });

      // Update the specifications data to include the new section title
      const updateSpecs = (prevData: any) => {
         const newSpecs = JSON.parse(JSON.stringify(prevData.specifications));
         
         // Update sectionTitles in specifications
         if (!newSpecs.sectionTitles) {
           newSpecs.sectionTitles = {};
         }
         newSpecs.sectionTitles[oldTitle] = newTitle;
         
         return { ...prevData, specifications: newSpecs };
      };

      if (showVersionFormModal) {
         setVersionFormData(updateSpecs);
      } else {
         setFormData(updateSpecs);
      }

      setEditingSectionTitle(null);
      setTempSectionTitle('');
    }
  }, [editingSectionTitle, tempSectionTitle, showVersionFormModal]);

  const handleCancelEditSectionTitle = useCallback(() => {
    setEditingSectionTitle(null);
    setTempSectionTitle('');
  }, []);

  // Handle Enter key in the section title input
  const handleSectionTitleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveSectionTitle();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEditSectionTitle();
    }
  }, [handleSaveSectionTitle, handleCancelEditSectionTitle]);

  const clearModelFilters = useCallback(() => {
    setModelSearchTerm('');
    setSelectedBrandFilter('');
    setSelectedStatusFilter('all');
    setSortOrder('price_asc');
  }, []);

  const clearVersionFilters = useCallback(() => {
    setVersionSearchTerm('');
    setSelectedVersionStatusFilter('all');
    setPriceRangeFilter({ min: '', max: '' });
  }, []);

  const openVersionsModal = useCallback(async (model: CarModelWithBrand) => {
    setSelectedModelForVersions(model);
    try {
      const versionsData = await carPricesApi.getModelVersions(model.id);
      setVersions(versionsData);
    } catch (error) {
      console.error('Error loading versions:', error);
    }
    setShowVersionsModal(true);
  }, []);

  const closeVersionFormModal = useCallback(() => {
    setShowVersionFormModal(false);
    setEditingVersion(null);
  }, []);

const handleVersionSubmit = useCallback(async (e: React.FormEvent) => {
  e.preventDefault();
  
  if (!selectedModelForVersions) return;
  
  try {
    setSubmitting(true);

    // Only include fields that exist in the database schema
    const versionData = {
      model_id: selectedModelForVersions.id,
      name: versionFormData.name,
      slug: generateSlug(versionFormData.name), // Add this line
      description: versionFormData.description || null,
      price: versionFormData.price ? parseFloat(versionFormData.price) : null,
      engine: versionFormData.engine || null,
      transmission: versionFormData.transmission || null,
      power: versionFormData.power || null,
      fuel_type: versionFormData.fuel_type || null,
      carrosserie: versionFormData.carrosserie || null,
      garantie: versionFormData.garantie || null,
      nombre_de_places: versionFormData.nombre_de_places || null,
      images: versionFormData.images.length > 0 ? versionFormData.images : null,
      features: versionFormData.features.length > 0 ? versionFormData.features : null,
      specifications: processSpecificationsForApi(versionFormData.specifications),
      is_active: versionFormData.is_active
    };

    if (editingVersion) {
      await carPricesApi.updateModelVersion(editingVersion.id, versionData);
    } else {
      await carPricesApi.createModelVersion(versionData);
    }

    const versionsData = await carPricesApi.getModelVersions(selectedModelForVersions.id);
    setVersions(versionsData);
    
    closeVersionFormModal();
    alert(editingVersion ? 'Version mise à jour avec succès' : 'Version créée avec succès');
  } catch (error) {
    console.error('Error saving version:', error);
    alert('Erreur lors de l\'enregistrement de la version');
  } finally {
    setSubmitting(false);
  }
}, [selectedModelForVersions, versionFormData, editingVersion, processSpecificationsForApi, closeVersionFormModal]);

  const handleDeleteVersion = useCallback(async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette version ?')) {
      return;
    }

    try {
      await carPricesApi.deleteModelVersion(id);
      
      if (selectedModelForVersions) {
        const versionsData = await carPricesApi.getModelVersions(selectedModelForVersions.id);
        setVersions(versionsData);
      }
      
      alert('Version supprimée avec succès');
    } catch (error) {
      console.error('Error deleting version:', error);
      alert('Erreur lors de la suppression de la version');
    }
  }, [selectedModelForVersions]);

  const openVersionFormModal = useCallback((version?: CarModelVersion) => {
    if (version) {
      setEditingVersion(version.id ? version : null); // Set editingVersion only if it has an id
      
      const loadedSpecs = loadSpecifications(version.specifications);

      setVersionFormData({
        name: version.name,
        description: version.description || '',
        price: version.price?.toString() || '',
        engine: version.engine || '',
        transmission: version.transmission || '',
        power: version.power || '',
        fuel_type: version.fuel_type || '',
        carrosserie: version.carrosserie || '',
        garantie: version.garantie || '',
        nombre_de_places: version.nombre_de_places || '',
        images: version.images || [],
        features: version.features || [],
        specifications: loadedSpecs,
        is_active: version.is_active
      });
    } else {
      setEditingVersion(null);
      
      const emptySpecs = loadSpecifications({}); // Get empty categorized spec object

      setVersionFormData({
        name: '',
        description: '',
        price: '',
        engine: '',
        transmission: '',
        power: '',
        fuel_type: '',
        carrosserie: '',
        garantie: '',
        nombre_de_places: '',
        images: [],
        features: [],
        specifications: emptySpecs,
        is_active: true
      });
    }
    setShowVersionFormModal(true);
  }, [loadSpecifications, sectionTitles]);

const handleSubmit = useCallback(async (e: React.FormEvent) => {
  e.preventDefault();
  
  try {
    setSubmitting(true);

    // Only include fields that exist in the database schema
    const modelData = {
      brand_id: formData.brand_id,
      name: formData.name,
      slug: generateSlug(formData.name), // Add this line
      description: formData.description || null,
      starting_price: formData.starting_price ? parseFloat(formData.starting_price) : null,
      engine: formData.engine || null,
      transmission: formData.transmission || null,
      power: formData.power || null,
      fuel_type: formData.fuel_type || null,
      carrosserie: formData.carrosserie || null,
      garantie: formData.garantie || null,
      nombre_de_places: formData.nombre_de_places || null,
      images: formData.images.length > 0 ? formData.images : null,
      features: formData.features.length > 0 ? formData.features : null,
      specifications: processSpecificationsForApi(formData.specifications),
      is_active: formData.is_active
    };

    if (editingModel) {
      await carPricesApi.updateModel(editingModel.id, modelData);
    } else {
      await carPricesApi.createModel(modelData);
    }

    await loadData();
    closeModal();
    alert(editingModel ? 'Modèle mis à jour avec succès' : 'Modèle créé avec succès');
  } catch (error) {
    console.error('Error saving model:', error);
    alert(`Erreur lors de l'enregistrement du modèle : ${(error as any)?.message || 'Erreur inconnue'}${(error as any)?.code ? ` (code ${(error as any).code})` : ''}`);
  } finally {
    setSubmitting(false);
  }
}, [formData, editingModel, processSpecificationsForApi]);

  const handleDelete = useCallback(async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce modèle ?')) {
      return;
    }

    try {
      await carPricesApi.deleteModel(id);
      await loadData();
      alert('Modèle supprimé avec succès');
    } catch (error) {
      console.error('Error deleting model:', error);
      alert('Erreur lors de la suppression du modèle');
    }
  }, []);

  const toggleActive = useCallback(async (model: CarModelWithBrand) => {
    try {
      await carPricesApi.updateModel(model.id, { is_active: !model.is_active });
      await loadData();
    } catch (error) {
      console.error('Error toggling model status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  }, []);

const handleDuplicateVersion = useCallback((version: CarModelVersion) => {
  const { id, ...versionWithoutId } = version;
  const duplicatedVersion = {
    ...versionWithoutId,
    name: `${version.name} (copie)`,
    slug: generateSlug(`${version.name} (copie)`), // Add this line
  };
  
  openVersionFormModal(duplicatedVersion as CarModelVersion);
}, [openVersionFormModal]);

  const openModal = useCallback((model?: CarModelWithBrand) => {
    if (model) {
      setEditingModel(model);
      const loadedSpecs = loadSpecifications(model.specifications);

      setFormData({
        brand_id: model.brand_id,
        name: model.name,
        description: model.description || '',
        starting_price: model.starting_price?.toString() || '',
        engine: model.engine || '',
        transmission: model.transmission || '',
        power: model.power || '',
        fuel_type: model.fuel_type || '',
        carrosserie: model.carrosserie || '',
        garantie: model.garantie || '',
        nombre_de_places: model.nombre_de_places || '',
        images: model.images || [],
        features: model.features || [],
        specifications: loadedSpecs,
        is_active: model.is_active
      });
    } else {
      setEditingModel(null);
      const emptySpecs = loadSpecifications({}); // Get empty categorized spec object

      setFormData({
        brand_id: '',
        name: '',
        description: '',
        starting_price: '',
        engine: '',
        transmission: '',
        power: '',
        fuel_type: '',
        carrosserie: '',
        garantie: '',
        nombre_de_places: '',
        images: [],
        features: [],
        specifications: emptySpecs,
        is_active: true
      });
    }
    setShowModal(true);
  }, [loadSpecifications]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingModel(null);
  }, []);

  // Setters for specifications
  const handleSetFormDataSpecifications = useCallback((newSpecs: Record<string, any>) => {
      setFormData(prev => ({ ...prev, specifications: newSpecs }));
  }, []);
  const handleSetVersionFormDataSpecifications = useCallback((newSpecs: Record<string, any>) => {
      setVersionFormData(prev => ({ ...prev, specifications: newSpecs }));
  }, []);

  const handleImageUpload = useCallback((url: string) => {
    setFormData(prev => ({ 
      ...prev, 
      images: [...prev.images, url]
    }));
  }, []);

  const removeImage = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  }, []);

  const removeFeature = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  }, []);

  const handleVersionImageUpload = useCallback((url: string) => {
    setVersionFormData(prev => ({ 
      ...prev, 
      images: [...prev.images, url]
    }));
  }, []);

  const removeVersionImage = useCallback((index: number) => {
    setVersionFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  }, []);

  const removeVersionFeature = useCallback((index: number) => {
    setVersionFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  }, []);

  const formatPrice = useCallback((price: number | null) => {
    if (!price) return 'Prix sur demande';
    return `${price.toLocaleString()} TND`;
  }, []);

  return (
    <AdminLayout title="Gestion des modèles de voitures">
      <div className="px-4 py-6 sm:px-0">
        <div className="border-4 border-dashed border-gray-200 rounded-lg p-6">
          {/* Header with filters */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Modèles de voitures</h1>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowModelFilters(!showModelFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filtres
              </button>
              <button
                onClick={() => openModal()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                style={{ backgroundColor: '#d4452b' }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter un modèle
              </button>
            </div>
          </div>

          {/* Model filters */}
          {showModelFilters && (
            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label htmlFor="model-search" className="block text-sm font-medium text-gray-700 mb-1">
                    Recherche
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="model-search"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      placeholder="Nom du modèle ou marque"
                      value={modelSearchTerm}
                      onChange={(e) => setModelSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="brand-filter" className="block text-sm font-medium text-gray-700 mb-1">
                    Marque
                  </label>
                  <select
                    id="brand-filter"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                    value={selectedBrandFilter}
                    onChange={(e) => setSelectedBrandFilter(e.target.value)}
                  >
                    <option value="">Toutes les marques</option>
                    {brands.map(brand => (
                      <option key={brand.id} value={brand.id}>{brand.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
                    Statut
                  </label>
                  <select
                    id="status-filter"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                    value={selectedStatusFilter}
                    onChange={(e) => setSelectedStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                  >
                    <option value="all">Tous</option>
                    <option value="active">Actifs</option>
                    <option value="inactive">Inactifs</option>
                  </select>
                </div>
                {/* NEW SORT DROPDOWN */}
                <div>
                  <label htmlFor="sort-order" className="block text-sm font-medium text-gray-700 mb-1">
                    Trier par
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    </div>
                    <select
                      id="sort-order"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value as 'price_asc' | 'recent')}
                    >
                      <option value="price_asc">Prix croissant</option>
                      <option value="recent">Récemment ajouté</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                  <button
                    onClick={clearModelFilters}
                    className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Effacer
                  </button>
              </div>
            </div>
          )}

          {/* Models table */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-lg text-gray-500">Chargement...</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modèle
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Marque
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prix de départ
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Garantie
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredModels.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                        Aucun modèle trouvé
                      </td>
                    </tr>
                  ) : (
                    filteredModels.map(model => (
                      <tr key={model.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {model.images && model.images.length > 0 && (
                              <div className="flex-shrink-0 h-10 w-10">
                                <img className="h-10 w-10 rounded-full object-cover" src={model.images[0]} alt="" />
                              </div>
                            )}
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{model.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{getBrandName(model.brand_id)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatPrice(model.starting_price)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{model.garantie || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            model.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {model.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openModal(model)}
                              className="text-red-600 hover:text-red-900"
                              style={{ color: '#d4452b' }}
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => openVersionsModal(model)}
                              className="text-green-600 hover:text-green-900"
                            >
                              <FileText className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => toggleActive(model)}
                              className="text-yellow-600 hover:text-yellow-900"
                            >
                              {model.is_active ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                            </button>
                            <button
                              onClick={() => handleDelete(model.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Model Form Modal */}
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
                  <div className="mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {editingModel ? 'Modifier le modèle' : 'Ajouter un nouveau modèle'}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="brand_id" className="block text-sm font-medium text-gray-700">
                        Marque
                      </label>
                      <select
                        id="brand_id"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={formData.brand_id}
                        onChange={(e) => setFormData({ ...formData, brand_id: e.target.value })}
                        required
                      >
                        <option value="">Sélectionner une marque</option>
                        {brands.map(brand => (
                          <option key={brand.id} value={brand.id}>{brand.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Nom du modèle
                      </label>
                      <input
                        type="text"
                        id="name"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="starting_price" className="block text-sm font-medium text-gray-700">
                        Prix de départ (TND)
                      </label>
                      <input
                        type="number"
                        id="starting_price"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={formData.starting_price}
                        onChange={(e) => setFormData({ ...formData, starting_price: e.target.value })}
                      />
                    </div>

                    <div>
                      <label htmlFor="garantie" className="block text-sm font-medium text-gray-700">
                        Garantie
                      </label>
                      <input
                        type="text"
                        id="garantie"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={formData.garantie}
                        onChange={(e) => setFormData({ ...formData, garantie: e.target.value })}
                        placeholder="Ex: 2 ans, 50000km"
                      />
                    </div>

                    <div>
                      <label htmlFor="nombre_de_places" className="block text-sm font-medium text-gray-700">
                        Nombre de places
                      </label>
                      <input
                        type="text"
                        id="nombre_de_places"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={formData.nombre_de_places}
                        onChange={(e) => setFormData({ ...formData, nombre_de_places: e.target.value })}
                        placeholder="Ex: 5"
                      />
                    </div>

                    <div>
                      <label htmlFor="engine" className="block text-sm font-medium text-gray-700">
                        Moteur
                      </label>
                      <input
                        type="text"
                        id="engine"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={formData.engine}
                        onChange={(e) => setFormData({ ...formData, engine: e.target.value })}
                      />
                    </div>

                    <div>
                      <label htmlFor="transmission" className="block text-sm font-medium text-gray-700">
                        Transmission
                      </label>
                      <input
                        type="text"
                        id="transmission"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={formData.transmission}
                        onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                      />
                    </div>

                    <div>
                      <label htmlFor="power" className="block text-sm font-medium text-gray-700">
                        Puissance
                      </label>
                      <input
                        type="text"
                        id="power"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={formData.power}
                        onChange={(e) => setFormData({ ...formData, power: e.target.value })}
                      />
                    </div>

                    <div>
                      <label htmlFor="fuel_type" className="block text-sm font-medium text-gray-700">
                        Type de carburant
                      </label>
                      <input
                        type="text"
                        id="fuel_type"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={formData.fuel_type}
                        onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}
                      />
                    </div>

                    <div>
                      <label htmlFor="carrosserie" className="block text-sm font-medium text-gray-700">
                        Carrosserie
                      </label>
                      <select
                        id="carrosserie"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={formData.carrosserie}
                        onChange={(e) => setFormData({ ...formData, carrosserie: e.target.value })}
                      >
                        <option value="">Sélectionner une carrosserie</option>
                        {carrosserieOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="is_active"
                        type="checkbox"
                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                      <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                        Actif
                      </label>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      id="description"
                      rows={3}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Images
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.images.map((image, index) => (
                        <div key={index} className="relative">
                          <img src={image} alt={`Image ${index + 1}`} className="h-20 w-20 object-cover rounded" />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 text-xs"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <ImageUpload onImageUploaded={handleImageUpload} />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Caractéristiques
                    </label>
                    <div className="mb-2">
                      <button
                        type="button"
                        onClick={replaceAllSpecifications}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Importer/Remplacer des caractéristiques
                      </button>
                    </div>
                    <SpecificationsDisplay
                       specifications={formData.specifications}
                       setSpecifications={handleSetFormDataSpecifications}
                       clearAllSpecifications={clearAllSpecifications}
                       sectionTitles={sectionTitles}
                       editingSectionTitle={editingSectionTitle}
                       tempSectionTitle={tempSectionTitle}
                       setTempSectionTitle={setTempSectionTitle}
                       handleEditSectionTitle={handleEditSectionTitle}
                       handleSaveSectionTitle={handleSaveSectionTitle}
                       handleCancelEditSectionTitle={handleCancelEditSectionTitle}
                       handleSectionTitleKeyDown={handleSectionTitleKeyDown}
                       expandedCategories={expandedCategories}
                       toggleCategoryExpansion={toggleCategoryExpansion}
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Équipements (Liste simple)
                    </label>
                    <div className="space-y-2">
                      {formData.features.map((feature, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span>{feature}</span>
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                    style={{ backgroundColor: '#d4452b' }}
                    disabled={submitting}
                  >
                    {submitting ? 'Enregistrement...' : (editingModel ? 'Mettre à jour' : 'Ajouter')}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={closeModal}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Versions Modal */}
      {showVersionsModal && selectedModelForVersions && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Versions du modèle: {selectedModelForVersions.name}
                  </h3>
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                    style={{ backgroundColor: '#d4452b' }}
                    onClick={() => openVersionFormModal()}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter une version
                  </button>
                </div>

                {/* Version filters */}
                <div className="bg-gray-50 p-4 rounded-md mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label htmlFor="version-search" className="block text-sm font-medium text-gray-700 mb-1">
                        Recherche
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="version-search"
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                          placeholder="Nom de la version"
                          value={versionSearchTerm}
                          onChange={(e) => setVersionSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="version-status-filter" className="block text-sm font-medium text-gray-700 mb-1">
                        Statut
                      </label>
                      <select
                        id="version-status-filter"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={selectedVersionStatusFilter}
                        onChange={(e) => setSelectedVersionStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                      >
                        <option value="all">Tous</option>
                        <option value="active">Actifs</option>
                        <option value="inactive">Inactifs</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="price-min" className="block text-sm font-medium text-gray-700 mb-1">
                        Prix min
                      </label>
                      <input
                        type="number"
                        id="price-min"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        placeholder="Prix minimum"
                        value={priceRangeFilter.min}
                        onChange={(e) => setPriceRangeFilter({ ...priceRangeFilter, min: e.target.value })}
                      />
                    </div>
                    <div>
                      <label htmlFor="price-max" className="block text-sm font-medium text-gray-700 mb-1">
                        Prix max
                      </label>
                      <input
                        type="number"
                        id="price-max"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        placeholder="Prix maximum"
                        value={priceRangeFilter.max}
                        onChange={(e) => setPriceRangeFilter({ ...priceRangeFilter, max: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={clearVersionFilters}
                      className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Effacer
                    </button>
                  </div>
                </div>

                {/* Versions table */}
                <div className="overflow-x-auto max-h-[50vh]">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Version
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Prix
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Garantie
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredVersions.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                            Aucune version trouvée
                          </td>
                        </tr>
                      ) : (
                        filteredVersions.map(version => (
                          <tr key={version.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {version.images && version.images.length > 0 && (
                                  <div className="flex-shrink-0 h-10 w-10">
                                    <img className="h-10 w-10 rounded-full object-cover" src={version.images[0]} alt="" />
                                  </div>
                                )}
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{version.name}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 truncate max-w-xs">{version.description || '-'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatPrice(version.price)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{version.garantie || '-'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                version.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {version.is_active ? 'Actif' : 'Inactif'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => openVersionFormModal(version)}
                                  className="text-red-600 hover:text-red-900"
                                  style={{ color: '#d4452b' }}
                                >
                                  <Edit2 className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleDuplicateVersion(version)}
                                  className="text-green-600 hover:text-green-900"
                                >
                                  <Copy className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteVersion(version.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <Trash2 className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowVersionsModal(false)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version Form Modal */}
      {showVersionFormModal && (
        <div className="fixed z-20 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <form onSubmit={handleVersionSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
                  <div className="mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {editingVersion ? 'Modifier la version' : 'Ajouter une nouvelle version'}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="version-name" className="block text-sm font-medium text-gray-700">
                        Nom de la version
                      </label>
                      <input
                        type="text"
                        id="version-name"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={versionFormData.name}
                        onChange={(e) => setVersionFormData({ ...versionFormData, name: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="version-price" className="block text-sm font-medium text-gray-700">
                        Prix (TND)
                      </label>
                      <input
                        type="number"
                        id="version-price"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={versionFormData.price}
                        onChange={(e) => setVersionFormData({ ...versionFormData, price: e.target.value })}
                      />
                    </div>

                    <div>
                      <label htmlFor="version-garantie" className="block text-sm font-medium text-gray-700">
                        Garantie
                      </label>
                      <input
                        type="text"
                        id="version-garantie"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={versionFormData.garantie}
                        onChange={(e) => setVersionFormData({ ...versionFormData, garantie: e.target.value })}
                        placeholder="Ex: 2 ans, 50000km"
                      />
                    </div>

                    <div>
                      <label htmlFor="version-nombre_de_places" className="block text-sm font-medium text-gray-700">
                        Nombre de places
                      </label>
                      <input
                        type="text"
                        id="version-nombre_de_places"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={versionFormData.nombre_de_places}
                        onChange={(e) => setVersionFormData({ ...versionFormData, nombre_de_places: e.target.value })}
                        placeholder="Ex: 5"
                      />
                    </div>

                    <div>
                      <label htmlFor="version-engine" className="block text-sm font-medium text-gray-700">
                        Moteur
                      </label>
                      <input
                        type="text"
                        id="version-engine"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={versionFormData.engine}
                        onChange={(e) => setVersionFormData({ ...versionFormData, engine: e.target.value })}
                      />
                    </div>

                    <div>
                      <label htmlFor="version-transmission" className="block text-sm font-medium text-gray-700">
                        Transmission
                      </label>
                      <input
                        type="text"
                        id="version-transmission"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={versionFormData.transmission}
                        onChange={(e) => setVersionFormData({ ...versionFormData, transmission: e.target.value })}
                      />
                    </div>

                    <div>
                      <label htmlFor="version-power" className="block text-sm font-medium text-gray-700">
                        Puissance
                      </label>
                      <input
                        type="text"
                        id="version-power"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={versionFormData.power}
                        onChange={(e) => setVersionFormData({ ...versionFormData, power: e.target.value })}
                      />
                    </div>

                    <div>
                      <label htmlFor="version-fuel_type" className="block text-sm font-medium text-gray-700">
                        Type de carburant
                      </label>
                      <input
                        type="text"
                        id="version-fuel_type"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={versionFormData.fuel_type}
                        onChange={(e) => setVersionFormData({ ...versionFormData, fuel_type: e.target.value })}
                      />
                    </div>

                    <div>
                      <label htmlFor="version-carrosserie" className="block text-sm font-medium text-gray-700">
                        Carrosserie
                      </label>
                      <select
                        id="version-carrosserie"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={versionFormData.carrosserie}
                        onChange={(e) => setVersionFormData({ ...versionFormData, carrosserie: e.target.value })}
                      >
                        <option value="">Sélectionner une carrosserie</option>
                        {carrosserieOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="version-is_active"
                        type="checkbox"
                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        checked={versionFormData.is_active}
                        onChange={(e) => setVersionFormData({ ...versionFormData, is_active: e.target.checked })}
                      />
                      <label htmlFor="version-is_active" className="ml-2 block text-sm text-gray-900">
                        Actif
                      </label>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label htmlFor="version-description" className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      id="version-description"
                      rows={3}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      value={versionFormData.description}
                      onChange={(e) => setVersionFormData({ ...versionFormData, description: e.target.value })}
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Images
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {versionFormData.images.map((image, index) => (
                        <div key={index} className="relative">
                          <img src={image} alt={`Image ${index + 1}`} className="h-20 w-20 object-cover rounded" />
                          <button
                            type="button"
                            onClick={() => removeVersionImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 text-xs"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <ImageUpload onImageUploaded={handleVersionImageUpload} />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Caractéristiques
                    </label>
                    <div className="mb-2">
                      <button
                        type="button"
                        onClick={replaceAllSpecifications}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Importer/Remplacer des caractéristiques
                      </button>
                    </div>
                    <SpecificationsDisplay
                       specifications={versionFormData.specifications}
                       setSpecifications={handleSetVersionFormDataSpecifications}
                       clearAllSpecifications={clearAllSpecifications}
                       sectionTitles={sectionTitles}
                       editingSectionTitle={editingSectionTitle}
                       tempSectionTitle={tempSectionTitle}
                       setTempSectionTitle={setTempSectionTitle}
                       handleEditSectionTitle={handleEditSectionTitle}
                       handleSaveSectionTitle={handleSaveSectionTitle}
                       handleCancelEditSectionTitle={handleCancelEditSectionTitle}
                       handleSectionTitleKeyDown={handleSectionTitleKeyDown}
                       expandedCategories={expandedCategories}
                       toggleCategoryExpansion={toggleCategoryExpansion}
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Équipements (Liste simple)
                    </label>
                    <div className="space-y-2">
                      {versionFormData.features.map((feature, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span>{feature}</span>
                          <button
                            type="button"
                            onClick={() => removeVersionFeature(index)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                    style={{ backgroundColor: '#d4452b' }}
                    disabled={submitting}
                  >
                    {submitting ? 'Enregistrement...' : (editingVersion ? 'Mettre à jour' : 'Ajouter')}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={closeVersionFormModal}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Characteristics Import Modal */}
      {showCharacteristicsModal && (
        <div className="fixed z-30 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
                <div className="mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Importer des caractéristiques
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Collez les caractéristiques ci-dessous. Elles seront analysées et organisées automatiquement.
                  </p>
                </div>

                <div className="mt-4">
                  <label htmlFor="characteristics-text" className="block text-sm font-medium text-gray-700">
                    Texte des caractéristiques
                  </label>
                  <textarea
                    id="characteristics-text"
                    rows={10}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                    value={characteristicsText}
                    onChange={(e) => setCharacteristicsText(e.target.value)}
                    placeholder="Collez les caractéristiques ici..."
                  />
                </div>

                {parseError && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Erreur d'analyse</h3>
                        <div className="mt-2 text-sm text-red-700">
                          <p>{parseError}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <button
                    type="button"
                    onClick={parseCharacteristics}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                    style={{ backgroundColor: '#d4452b' }}
                  >
                    Analyser
                  </button>
                </div>

                {parsedCharacteristics.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-2">Caractéristiques analysées</h4>
                    <div className="border border-gray-200 rounded-md overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Clé
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Valeur
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {parsedCharacteristics.map((characteristic, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {editingCharacteristic?.key === characteristic.key && editingCharacteristic?.value === characteristic.value ? (
                                  <input
                                    type="text"
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                                    value={tempCharacteristicDisplayKey}
                                    onChange={(e) => setTempCharacteristicDisplayKey(e.target.value)}
                                  />
                                ) : (
                                  characteristic.displayKey
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {editingCharacteristic?.key === characteristic.key && editingCharacteristic?.value === characteristic.value ? (
                                  <input
                                    type="text"
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                                    value={tempCharacteristicValue}
                                    onChange={(e) => setTempCharacteristicValue(e.target.value)}
                                  />
                                ) : (
                                  characteristic.value
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                {editingCharacteristic?.key === characteristic.key && editingCharacteristic?.value === characteristic.value ? (
                                  <div className="flex space-x-2">
                                    <button
                                      type="button"
                                      onClick={handleSaveEditedCharacteristic}
                                      className="text-green-600 hover:text-green-900"
                                    >
                                      <Check className="h-5 w-5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingCharacteristic(null)}
                                      className="text-gray-600 hover:text-gray-900"
                                    >
                                      <X className="h-5 w-5" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex space-x-2">
                                    <button
                                      type="button"
                                      onClick={() => handleEditCharacteristic(index)}
                                      className="text-blue-600 hover:text-blue-900"
                                    >
                                      <Edit2 className="h-5 w-5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteCharacteristic(characteristic.key, characteristic.value)}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="mt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-2">Ajouter une caractéristique</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="new-key" className="block text-sm font-medium text-gray-700">
                        Clé
                      </label>
                      <input
                        type="text"
                        id="new-key"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={tempCharacteristicDisplayKey}
                        onChange={(e) => setTempCharacteristicDisplayKey(e.target.value)}
                        placeholder="Ex: Garantie"
                      />
                    </div>
                    <div>
                      <label htmlFor="new-value" className="block text-sm font-medium text-gray-700">
                        Valeur
                      </label>
                      <input
                        type="text"
                        id="new-value"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                        value={tempCharacteristicValue}
                        onChange={(e) => setTempCharacteristicValue(e.target.value)}
                        placeholder="Ex: 2 ans"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddCharacteristic}
                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {parsedCharacteristics.length > 0 && (
                  <button
                    type="button"
                    onClick={handleApplyCharacteristics}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Appliquer les caractéristiques
                  </button>
                )}
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowCharacteristicsModal(false)}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}